import type React from "react";
import {
  createContext,
  type Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { handleLanguageChange } from "@app/internationalization/utils/handleLanguageChange";
import resources from "@static/translations/types/resources";

export enum EnabledLanguages {
  EN = "en-GB",
}

i18n.on("languageChanged", handleLanguageChange);

i18n.use(initReactI18next).init({
  // debug: import.meta.env.DEV,
  fallbackLng: "en-GB",
  defaultNS: "global",
  resources: { "en-GB": resources },
  load: "currentOnly",
  interpolation: { escapeValue: false },
});
export { i18n };

const InternationalizationContext = createContext<{
  i18n: typeof i18n;
  changeLanguage: Dispatch<EnabledLanguages>;
} | null>(null);

export const useInternationalization = () => {
  const context = useContext(InternationalizationContext);

  if (!context)
    throw new Error(
      "useInternationalization can only be used inside the InternationalizationProvider",
    );

  return context;
};

interface InternationalizationProviderProps {
  children?: React.ReactNode;
}

export const InternationalizationProvider: React.FC<
  InternationalizationProviderProps
> = ({ children }) => {
  const changeLanguage = useCallback((language: EnabledLanguages) => {
    localStorage.setItem("InternationalizationStore@language", language);
    i18n.changeLanguage(language);
  }, []);

  useEffect(() => {
    i18n.changeLanguage(
      localStorage.getItem("InternationalizationStore@language") ||
        EnabledLanguages.EN,
    );
  }, []);

  const value = useMemo(() => ({ i18n, changeLanguage }), [changeLanguage]);

  return (
    <InternationalizationContext.Provider value={value}>
      {children}
    </InternationalizationContext.Provider>
  );
};
